import { useCallback, useMemo } from 'react';

import {
  REMOVE_ANNOTATIONS,
  REORDER_ANNOTATIONS_STACKING,
  SELECT_ANNOTATION,
  SET_ANNOTATION,
  UPDATE_STATE,
} from '../actions';
import { isAnnotationLocked } from '../utils/annotationLockUtils';
import { WATERMARK_ANNOTATION_ID } from '../utils/constants';
import getLayerDisplayLabel from '../utils/getLayerDisplayLabel';
import {
  getLayerIdsForDisplay,
  moveLayerInDisplayOrder,
} from '../utils/moveLayerInDisplayOrder';
import useStore from './useStore';

const useLayersPanel = () => {
  const {
    t,
    dispatch,
    annotations = {},
    selectionsIds = [],
  } = useStore();

  const displayLayerIds = useMemo(
    () => getLayerIdsForDisplay(annotations),
    [annotations],
  );

  const layers = useMemo(
    () =>
      displayLayerIds.map((id) => {
        const annotation = annotations[id];
        return {
          id,
          annotation,
          label: getLayerDisplayLabel(annotation, t),
          isSelected: selectionsIds.includes(id),
          isLocked: isAnnotationLocked(annotation),
          isVisible: annotation?.visible !== false,
        };
      }),
    [annotations, displayLayerIds, selectionsIds, t],
  );

  const selectLayer = useCallback(
    (layerId, { multiple = false } = {}) => {
      if (!annotations[layerId]) {
        return;
      }

      dispatch({
        type: SELECT_ANNOTATION,
        payload: {
          annotationId: layerId,
          multiple,
        },
      });
    },
    [annotations, dispatch],
  );

  const toggleLayerVisibility = useCallback(
    (layerId) => {
      const annotation = annotations[layerId];
      if (!annotation) {
        return;
      }

      const nextVisible = annotation.visible === false;

      dispatch({
        type: SET_ANNOTATION,
        payload: {
          id: layerId,
          visible: nextVisible,
        },
      });

      if (!nextVisible && selectionsIds.includes(layerId)) {
        const remaining = selectionsIds.filter((id) => id !== layerId);
        dispatch({
          type: UPDATE_STATE,
          payload: { selectionsIds: remaining },
        });
      }
    },
    [annotations, dispatch, selectionsIds],
  );

  const removeLayer = useCallback(
    (layerId) => {
      const annotation = annotations[layerId];
      if (
        !annotation ||
        layerId === WATERMARK_ANNOTATION_ID ||
        isAnnotationLocked(annotation)
      ) {
        return false;
      }

      dispatch({
        type: REMOVE_ANNOTATIONS,
        payload: {
          annotationsIds: [layerId],
          isDesignState: true,
        },
      });

      return true;
    },
    [annotations, dispatch],
  );

  const moveLayerToDisplayIndex = useCallback(
    (layerId, toDisplayIndex) => {
      const annotation = annotations[layerId];
      if (!annotation || isAnnotationLocked(annotation)) {
        return false;
      }

      const nextAnnotations = moveLayerInDisplayOrder(
        annotations,
        layerId,
        toDisplayIndex,
      );

      if (!nextAnnotations) {
        return false;
      }

      dispatch({
        type: REORDER_ANNOTATIONS_STACKING,
        payload: {
          annotationIds: [layerId],
          direction: 'forward',
          nextAnnotations,
        },
      });

      return true;
    },
    [annotations, dispatch],
  );

  return {
    layers,
    layerCount: layers.length,
    selectLayer,
    toggleLayerVisibility,
    removeLayer,
    moveLayerToDisplayIndex,
  };
};

export default useLayersPanel;
